import React, { useState, useEffect, useCallback } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import moment from 'moment-timezone';

const SecurityCodeManager = () => {
    // --- Data States ---
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [secretKeyRequests, setSecretKeyRequests] = useState([]);
    const [reviewingRequestId, setReviewingRequestId] = useState(null);
    const [generatedCredential, setGeneratedCredential] = useState(null);

    // --- Modal & Form States ---
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingCode, setEditingCode] = useState(null);

    // --- Add/Edit Form State ---
    const initialFormState = {
        name: '',
        code: '',
        botCode: '', // নতুন Bot Code যুক্ত করা হয়েছে
        limitAmount: 0.00,
        withdrawPercent: 80, 
    };
    const [form, setForm] = useState(initialFormState);

    // =========================================================
    // --- Data Fetcher & Confirmation Handler ---
    // =========================================================
    const fetchData = useCallback(() => {
        setLoading(true);
        setError(null);
        socketService.emit('fetchSecurityCodes');
    }, []);

    const fetchSecretKeyRequests = useCallback(() => {
        socketService.emit('admin-get-secret-key-requests', { status: 'pending' });
    }, []);
    
    const handleConfirmation = (data) => {
        setLoading(false);
        if (data.success) {
            setSuccessMessage(data.message || '✅ Operation successful!');
            setError(null);
            fetchData(); // Refresh list
            setTimeout(() => setSuccessMessage(null), 3000); // Auto hide success msg
        } else {
            setError(data.message || '❌ Failed: Unknown error');
            setSuccessMessage(null);
        }
    };

    // =========================================================
    // --- Socket Listeners (UPDATED) ---
    // =========================================================
    useEffect(() => {
        const socket = socketService.getSocket();
        if (!socket || !socket.connected) {
            socketService.connect();
        }

        const handleConnect = () => {
            fetchData();
            fetchSecretKeyRequests();
        };

        const handleCodesData = (data) => {
            setLoading(false);
            if (data.error) {
                setError(data.error);
                return;
            }
            const sortedCodes = (data.securityCodes || []).sort((a, b) => 
                moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
            );
            setCodes(sortedCodes);
        };

        const handleSecretKeyRequests = (data) => {
            if (data.success) setSecretKeyRequests(data.requests || []);
            else if (data.message) setError(data.message);
        };

        const handleSecretKeyReview = (data) => {
            setReviewingRequestId(null);
            if (data.success) {
                setSuccessMessage(data.message || 'Secret Key request reviewed.');
                if (data.action === 'approve' && data.request) setGeneratedCredential(data.request);
                setError(null);
                fetchSecretKeyRequests();
                fetchData();
                setTimeout(() => setSuccessMessage(null), 3000);
            } else {
                setError(data.message || 'Secret Key request review failed.');
            }
        };

        const handleSecretKeyRequestsChanged = () => fetchSecretKeyRequests();

        socketService.on('connect', handleConnect); 
        socketService.on('securityCodesList', handleCodesData);
        socketService.on('admin-secret-key-requests-response', handleSecretKeyRequests);
        socketService.on('admin-review-secret-key-request-response', handleSecretKeyReview);
        socketService.on('secret-key-requests-changed', handleSecretKeyRequestsChanged);
        
        socketService.on('securityCodes-add-response', (data) => {
            handleConfirmation(data);
            if (data.success) {
                setForm(initialFormState);
                closeModal();
            }
        });
        
        socketService.on('securityCodes-edit-response', (data) => {
            handleConfirmation(data);
            if (data.success) {
                closeModal();
            }
        });
        
        socketService.on('securityCodes-delete-response', handleConfirmation);
        
        if (socket && socket.connected) {
            fetchData();
            fetchSecretKeyRequests();
        }

        return () => {
            socketService.off('connect', handleConnect); 
            socketService.off('securityCodesList', handleCodesData);
            socketService.off('admin-secret-key-requests-response', handleSecretKeyRequests);
            socketService.off('admin-review-secret-key-request-response', handleSecretKeyReview);
            socketService.off('secret-key-requests-changed', handleSecretKeyRequestsChanged);
            socketService.off('securityCodes-add-response', handleConfirmation);
            socketService.off('securityCodes-edit-response', handleConfirmation);
            socketService.off('securityCodes-delete-response', handleConfirmation);
        };
    }, [fetchData, fetchSecretKeyRequests]);

    // =========================================================
    // --- Handlers ---
    // =========================================================

    const openAddModal = () => {
        setEditingCode(null);
        setForm(initialFormState);
        setShowFormModal(true);
    }
    
    const openEditModal = (codeData) => {
        setEditingCode({ ...codeData, id: codeData._id || codeData.id }); 
        setForm({
            name: codeData.name,
            code: codeData.code,
            botCode: codeData.botCode || '', // এডিট করার সময় Bot Code লোড হবে
            limitAmount: codeData.limitAmount,
            withdrawPercent: codeData.withdrawPercent !== undefined ? codeData.withdrawPercent : 80,
        });
        setShowFormModal(true);
    };

    const closeModal = () => {
        setShowFormModal(false);
        setEditingCode(null);
        setForm(initialFormState);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ 
            ...prev, 
            [name]: (name === 'limitAmount' || name === 'withdrawPercent' ? parseFloat(value) : value) 
        }));
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (!form.name || !form.code || form.limitAmount <= 0) {
            alert('Name, Code are required and Limit Amount must be greater than 0.');
            return;
        }
        setLoading(true);
        const dataToSend = { ...form };
        if (editingCode) {
            dataToSend.id = editingCode.id;
            socketService.emit('securityCodes-edit', dataToSend);
        } else {
            socketService.emit('securityCodes-add', dataToSend);
        }
    };

    const handleDelete = (id) => {
        if (!window.confirm('Are you sure you want to delete this Security Code?')) return;
        setLoading(true);
        socketService.emit('securityCodes-delete', { id });
    };

    const reviewSecretKeyRequest = (requestId, action) => {
        if (reviewingRequestId) return;
        if (!window.confirm(action === 'approve'
            ? 'Approve this Secret Key request and generate the site credentials?'
            : 'Reject this Secret Key request?')) return;
        setReviewingRequestId(requestId);
        socketService.emit('admin-review-secret-key-request', { requestId, action });
    };

    // =========================================================
    // --- JSX Render ---
    // =========================================================
    return (
        <Layout>
            <div className="main-content-area py-3 px-4">
                
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="section-title mb-0">🔐 Security Codes Manager</h4>
                    <div className="count-badge">Total Codes: <span>{codes.length}</span></div>
                </div>

                {/* Alerts */}
                {successMessage && <div className="alert alert-success py-2 px-3 mb-3 small shadow-sm border-0 bg-seen">{successMessage}</div>}
                {error && <div className="alert alert-danger py-2 px-3 mb-3 small shadow-sm border-0 bg-fake">{error}</div>}
                {generatedCredential && (
                    <div className="alert alert-success mb-3 shadow-sm">
                        <div className="d-flex justify-content-between align-items-start gap-3">
                            <div className="flex-grow-1">
                                <div className="fw-bold mb-2">Generated credentials — {generatedCredential.siteName}</div>
                                <div className="small text-break"><strong>Public Key:</strong> <code>{generatedCredential.publicKey}</code></div>
                                <div className="small text-break mt-1"><strong>Secret Key:</strong> <code>{generatedCredential.secretKey}</code></div>
                            </div>
                            <div className="d-flex gap-2 flex-wrap justify-content-end">
                                <button className="btn btn-sm btn-outline-success" onClick={() => navigator.clipboard.writeText(generatedCredential.publicKey)}>Copy Public</button>
                                <button className="btn btn-sm btn-success" onClick={() => navigator.clipboard.writeText(generatedCredential.secretKey)}>Copy Secret</button>
                                <button className="btn btn-sm btn-light" onClick={() => setGeneratedCredential(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="table-container shadow-sm mb-4">
                    <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                        <div>
                            <div className="fw-bold text-dark">Pending Secret Key Requests</div>
                            <small className="text-muted">Approve one identity for each website and device.</small>
                        </div>
                        <span className="count-badge">Pending: <span>{secretKeyRequests.length}</span></span>
                    </div>
                    <div className="table-responsive">
                        <table className="custom-zebra-table">
                            <thead>
                                <tr>
                                    <th>User / Device</th>
                                    <th>Website</th>
                                    <th>Domain</th>
                                    <th>Requested</th>
                                    <th className="text-end">Decision</th>
                                </tr>
                            </thead>
                            <tbody>
                                {secretKeyRequests.length ? secretKeyRequests.map(request => (
                                    <tr key={request.id}>
                                        <td>
                                            <div className="user-primary">{request.username}</div>
                                            <div className="time-muted">{request.deviceName}</div>
                                        </td>
                                        <td><span className="fw-semibold">{request.siteName}</span></td>
                                        <td><code>{request.domain}</code></td>
                                        <td>
                                            <div className="date-bold">{moment(request.createdAt).format('YYYY-MM-DD')}</div>
                                            <div className="time-muted">{moment(request.createdAt).format('hh:mm A')}</div>
                                        </td>
                                        <td className="text-end">
                                            <button
                                                className="btn btn-sm btn-success me-2"
                                                disabled={reviewingRequestId === request.id}
                                                onClick={() => reviewSecretKeyRequest(request.id, 'approve')}
                                            >Approve</button>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                disabled={reviewingRequestId === request.id}
                                                onClick={() => reviewSecretKeyRequest(request.id, 'reject')}
                                            >Reject</button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="5" className="text-center py-4 text-muted">No pending Secret Key requests.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                {/* Action Bar */}
                <div className="filter-card mb-4 shadow-sm d-flex justify-content-between align-items-center">
                    <div>
                        <span className="field-label d-block">QUICK ACTIONS</span>
                        <small className="text-muted">Manage your verification codes and limits</small>
                    </div>
                    <button onClick={openAddModal} className="btn-apply py-2 px-4">➕ Add New Code</button>
                </div>

                {/* Table Section */}
                <div className="table-container shadow-sm">
                    {loading ? (
                        <div className="text-center p-5 text-muted">Loading data...</div>
                    ) : (
                        <table className="custom-zebra-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Security Code</th>
                                    <th>Bot Code</th> {/* নতুন কলাম */}
                                    <th>Owner</th> {/* কোন user এই code এর মালিক */}
                                    <th>Limit Amount</th>
                                    <th>Withdraw %</th>
                                    <th>Created At</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {codes.length > 0 ? codes.map((c, index) => (
                                    <tr key={c._id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <span className="user-primary">{c.name}</span>
                                        </td>
                                        <td>
                                            <div className="device-secondary px-2 py-1" style={{fontSize: '12px'}}>
                                                {c.code}
                                            </div>
                                        </td>
                                        <td>
                                            {/* Bot Code দেখানোর জায়গা */}
                                            {c.botCode ? (
                                                <div className="device-secondary px-2 py-1" style={{fontSize: '12px', backgroundColor: '#e0f2fe', color: '#0284c7'}}>
                                                    {c.botCode}
                                                </div>
                                            ) : (
                                                <span className="text-muted" style={{fontSize: '11px'}}>N/A</span>
                                            )}
                                        </td>
                                        <td>
                                            {/* Owner: কোন user এই code এ প্রথম connect করে bind করেছে */}
                                            {c.ownerUsername ? (
                                                <span className="device-secondary px-2 py-1" style={{fontSize: '12px', backgroundColor: '#dcfce7', color: '#16a34a'}}>
                                                    👤 {c.ownerUsername}
                                                </span>
                                            ) : (
                                                <span className="text-muted" style={{fontSize: '11px'}}>— unbound —</span>
                                            )}
                                        </td>
                                        <td>
                                            <span style={{color: '#16a34a', fontWeight: '700'}}>
                                                ৳{parseFloat(c.limitAmount).toFixed(2)}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{color: '#eab308', fontWeight: '700'}}>
                                                {c.withdrawPercent}%
                                            </span>
                                        </td>
                                        <td>
                                            <div className="date-bold">{moment(c.createdAt).format('YYYY-MM-DD')}</div>
                                            <div className="time-muted">{moment(c.createdAt).format('hh:mm A')}</div>
                                        </td>
                                        <td className="text-end">
                                            <button 
                                                className="btn btn-sm btn-light border me-1" 
                                                style={{fontSize: '11px', fontWeight: '600', color: '#0369a1'}}
                                                onClick={() => openEditModal(c)}
                                            >
                                                ✏️ Edit
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-light border text-danger" 
                                                style={{fontSize: '11px', fontWeight: '600'}}
                                                onClick={() => handleDelete(c._id)}
                                            >
                                                🗑 Delete
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="9" className="text-center py-4 text-muted">No security codes found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showFormModal && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal-dialog">
                        <div className="custom-modal-content">
                            <div className="modal-header bg-light">
                                <h6 className="m-0 fw-bold">
                                    {editingCode ? '✏️ Edit Security Code' : '➕ Add Security Code'}
                                </h6>
                                <button type="button" className="btn-close" onClick={closeModal}></button>
                            </div>
                            
                            <form onSubmit={handleSave}>
                                <div className="modal-body p-4">
                                    <div className="mb-3">
                                        <label className="field-label">Name</label>
                                        <input 
                                            type="text" 
                                            name="name" 
                                            value={form.name} 
                                            onChange={handleFormChange} 
                                            className="modern-input" 
                                            placeholder="e.g. Agent 01"
                                            required 
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="field-label">Code</label>
                                            <input 
                                                type="text" 
                                                name="code" 
                                                value={form.code} 
                                                onChange={handleFormChange} 
                                                className="modern-input" 
                                                placeholder="e.g. 123456"
                                                required 
                                            />
                                        </div>
                                        
                                        {/* Bot Code এর ইনপুট ফিল্ড যুক্ত করা হয়েছে */}
                                        <div className="col-md-6 mb-3">
                                            <label className="field-label">Bot Code (Optional)</label>
                                            <input 
                                                type="text" 
                                                name="botCode" 
                                                value={form.botCode} 
                                                onChange={handleFormChange} 
                                                className="modern-input" 
                                                placeholder="e.g. BOT-123"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="row">
                                        <div className="col-md-6 mb-4">
                                            <label className="field-label">Limit Amount</label>
                                            <input 
                                                type="number" 
                                                step="0.01" 
                                                name="limitAmount" 
                                                value={form.limitAmount} 
                                                onChange={handleFormChange} 
                                                className="modern-input" 
                                                placeholder="0.00"
                                                required 
                                            />
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="field-label">Withdraw Percent (%)</label>
                                            <input 
                                                type="number" 
                                                step="0.01" 
                                                name="withdrawPercent" 
                                                value={form.withdrawPercent} 
                                                onChange={handleFormChange} 
                                                className="modern-input" 
                                                placeholder="80"
                                                required 
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-reset flex-fill py-2" onClick={closeModal}>Cancel</button>
                                    <button type="submit" className="btn btn-apply flex-fill py-2" disabled={loading}>
                                        {loading ? 'Saving...' : (editingCode ? 'Update Code' : 'Save Code')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; }
                .count-badge span { color: #0284c7; font-weight: 800; }
                .border-success-subtle { border-color: #bbf7d0 !important; }
                .filter-card { background: #ffffff; padding: 15px; border-radius: 12px; }
                .field-label { font-size: 10px; font-weight: 700; color: #64748b; margin-bottom: 5px; text-transform: uppercase; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 13px; transition: 0.3s; border: none; }
                .btn-apply:hover { background: #0369a1; transform: translateY(-1px); }
                .btn-reset { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; transition: 0.3s; }
                .btn-reset:hover { background: #e2e8f0; }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #e2e8f0; color: #475569; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #cbd5e1; text-align: left; }
                .custom-zebra-table tbody tr:nth-child(even) { background-color: #f8fafc; }
                .custom-zebra-table td { padding: 12px 15px; vertical-align: middle; border-bottom: 1px solid #edf2f7; }
                .user-primary { color: #0369a1; font-weight: 700; font-size: 14px; }
                .device-secondary { font-size: 10px; color: #0000009d; font-weight: 800; background: #e2e8f0; display: inline-block; padding: 1px 6px; border-radius: 4px; margin-top: 3px; }
                .sim-num { color: #1e293b; font-weight: 600; font-size: 13px; }
                .msg-body { color: #334155; font-size: 12.5px; line-height: 1.5; }
                .date-bold { color: #334155; font-weight: 700; font-size: 13px; }
                .time-muted { font-size: 11px; color: #64748b; }
                .bg-seen { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
                .bg-fake { background: #fee2e2; color: #dc2626; border-color: #fecaca; }

                /* MODAL STYLES */
                .custom-modal-overlay {
                    position: fixed; 
                    top: 0; left: 0; right: 0; bottom: 0;
                    width: 100%; height: 100%;
                    background: rgba(0, 0, 0, 0.7); 
                    z-index: 99999 !important; 
                    display: flex; 
                    justify-content: center;
                    align-items: flex-start;
                    padding-top: 60px;
                    overflow-y: auto; 
                }

                .custom-modal-dialog {
                    background: #fff; 
                    width: 90%;            
                    max-width: 550px;      
                    margin: 0 auto 50px auto; 
                    border-radius: 12px; 
                    box-shadow: 0 20px 40px rgba(0,0,0,0.4);
                    position: relative;
                    animation: slideUp 0.3s ease-out;
                    display: flex;
                    flex-direction: column;
                }

                .custom-modal-content {
                    border: none;
                    overflow: hidden;
                    border-radius: 12px;
                    width: 100%;
                }

                .modal-header {
                    padding: 15px 20px; 
                    border-bottom: 1px solid #f1f5f9;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    background: #ffffff;
                    border-radius: 12px 12px 0 0;
                }

                .modal-body {
                    padding: 20px; 
                    overflow-x: hidden; 
                }
                
                .modal-footer {
                    padding: 15px 20px;
                    border-top: none;
                    display: flex;
                    gap: 10px;
                    background: #fff;
                    border-radius: 0 0 12px 12px;
                }

                .btn-close {
                    font-size: 12px;
                    background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414-1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat;
                    width: 1em; height: 1em; padding: 0.5em;
                    opacity: 0.5; border: 0; cursor: pointer;
                }
                .btn-close:hover { opacity: 0.8; }

                @keyframes slideUp { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            `}</style>
        </Layout>
    );
};

export default SecurityCodeManager;
